import * as React from "react";
import SwipeableDrawer from "@material-ui/core/SwipeableDrawer";
import styled from "styled-components";
import { css } from "aphrodite";
import { withStyles, WithStyles } from "@material-ui/core";

import ThemeContext from "themes/ThemeContext";
import { PaperProps } from "@material-ui/core/Paper";
import { styles } from "themes/materialUI";
import { StyledCloseIcon } from "../styled/StyledCloseIcon";

import { drawerStyles } from "./DrawerStyles";
import { useThemedStyleSheet } from "util/hooks/useThemedStyleSheet";

export type DrawerAnchorType = "bottom" | "left" | "right" | "top";

export interface DrawerCustomProps extends WithStyles<typeof styles> {
  internalVariant?: string;
  anchor?: DrawerAnchorType;
  disableBackdrop?: boolean;
  overrideClasses?: {
    container?: string;
    content?: string;
  };
  content: any;
  id: string;
  onClose?: () => void;
  onOpen?: () => void;
  open: boolean;
  paperProps?: PaperProps;
  showCloseIcon?: boolean;
}

// @FIXME: `allClasses: any` should use the correct typing
const getContainerClasses = (overrideClasses: { container?: string; content?: string } = {}, allClasses: any) => {
  if (overrideClasses.container) {
    return allClasses[overrideClasses.container];
  }
  return allClasses.drawerContainerStyles;
};

// @FIXME: `allClasses: any` should use the correct typing
const getContentClasses = (
  overrideClasses: { container?: string; content?: string } = {},
  allClasses: any,
  darkMode: boolean,
) => {
  if (overrideClasses.content) {
    return allClasses[overrideClasses.content];
  }

  if (darkMode) {
    return allClasses.drawerStylesDark;
  }
  return allClasses.drawerStyles;
};

const CloseIconWrapper = styled.div`
  position: absolute;
  top: 17px;
  right: 15px;
`;

const DivWithNoFocusOutline = styled.div`
  :focus {
    outline: 0;
  }
`;

/**
 * Drawer component which relies on MUI. This component is used with
 * the majority of Drawer features throughout the app.
 */
const DrawerCustom = ({
  anchor,
  classes,
  content,
  disableBackdrop,
  onClose = () => {},
  onOpen = () => {},
  open,
  overrideClasses,
  paperProps = {},
  showCloseIcon = true,
}: DrawerCustomProps) => {
  const { darkMode } = React.useContext(ThemeContext);
  const styles = useThemedStyleSheet(drawerStyles);

  /**
   * handles closing the drawer using `onClose` from the MUI component
   * also clears **NewForm** Context using `resetForm`
   */
  const handleCloseDrawerAndResetNewFormContext = (e: any, onClose) => {
    return onClose(e);
  };

  return (
    <div className="col d-flex align-items-center">
      <SwipeableDrawer
        anchor={anchor || "bottom"}
        BackdropProps={{ invisible: disableBackdrop }}
        className={getContainerClasses(overrideClasses, classes)}
        onClose={onClose}
        onOpen={onOpen}
        open={open}
        PaperProps={paperProps}
        SwipeAreaProps={{ className: css(styles.disableSwipe) }}
      >
        <DivWithNoFocusOutline
          tabIndex={0}
          role="button"
          className={getContentClasses(overrideClasses, classes, darkMode)}
        >
          {content}
          {showCloseIcon && (
            <CloseIconWrapper
              onClick={(e) => {
                handleCloseDrawerAndResetNewFormContext(e, onClose);
              }}
            >
              <StyledCloseIcon />
            </CloseIconWrapper>
          )}
        </DivWithNoFocusOutline>
      </SwipeableDrawer>
    </div>
  );
};

const Drawer = withStyles(styles)(DrawerCustom);

export { Drawer };
